/**
 * useEquipmentUnit — a `equipment/01-unit-editor` read-hook (seam #353/#354).
 * A `getEquipmentUnit` runtime-validál a `equipmentUnitReadResponseSchema`-ra.
 */
import { getEquipmentUnit } from '@/api/equipment-unit';
import type { EquipmentUnitRead } from '@kgc/equipment-type-domain';
import { useCallback, useEffect, useState } from 'react';

interface UseEquipmentUnitResult {
  unit: EquipmentUnitRead | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useEquipmentUnit(id: string | undefined): UseEquipmentUnitResult {
  const [unit, setUnit] = useState<EquipmentUnitRead | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUnit = useCallback(async () => {
    if (!id) {
      setUnit(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      setUnit(await getEquipmentUnit(id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch equipment unit';
      setError(message);
      console.error('Error fetching equipment unit:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchUnit();
  }, [fetchUnit]);

  return { unit, isLoading, error, refetch: fetchUnit };
}
